<template>
  <div class="bg-primary-container rounded-extra-large flex w-full flex-col overflow-hidden">
    <div class="w-full self-start overflow-hidden">
      <div class="flex flex-col">
        <div class="flex flex-row items-center py-4 pr-4 pl-2">
          <BaseIconButton icon="arrow_back" @click="() => emits('skilUnselected')" />
          <h2 class="text-headline-medium text-on-primary-container ml-3">
            {{ skill.title }}
          </h2>
        </div>
        <div class="relative mt-7 flex aspect-square w-full flex-row items-end">
          <DistributeItems
            class="absolute! top-0 left-0 z-10 w-full"
            :style="{ bottom: 100 / scale.items.length + '%' }"
            :size="56"
            :points="items.map((m) => ({ item: m.item, value: m.value, groupKey: m.item.key }))"
            v-slot="{ item }"
          >
            <template v-if="item.member">
              <BaseMemberAvatarShaped
                :color="skill.color"
                size="56"
                :member="item.member"
                :all-perspectives="topicPerspectives ?? []"
                :perspective="item.key"
              />
            </template>
            <div
              v-else
              class="relative flex h-14 w-14 items-center justify-center"
              v-tooltip="item.label"
            >
              <PerspectiveShape
                :all-perspectives="topicPerspectives ?? []"
                :perspective="item.key"
                class="h-13! w-13! *:border-2"
              />
              <BaseIcon
                class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                :class="`text-${skill.color}-30`"
                >person</BaseIcon
              >
            </div>
          </DistributeItems>
          <div
            class="bg-primary rounded-t-extra-large rounded-b-small ease-fast-spatial min-h-3 w-full transition-all duration-500"
            :style="{ height: height }"
          ></div>
          <span
            v-if="mainValue != null"
            class="text-display-medium text-on-primary-container absolute bottom-4 left-4 font-bold"
            >{{ useDisplayPercentage(mainValue) }}</span
          >
          <div class="absolute inset-0 flex w-full flex-col items-stretch">
            <div
              v-for="i in scale.items.length"
              :key="i"
              class="border-t-surface w-full grow border-t"
            ></div>
          </div>
          <div
            class="text-body-small text-on-primary-container absolute inset-0 flex h-full w-full flex-col items-end justify-between px-4 py-1"
          >
            <span>{{ scale.items[scale.items.length - 1].label }}</span>
            <span>{{ scale.items[0].label }}</span>
          </div>
        </div>
      </div>
    </div>
    <SkillLegend
      :color="skill.color"
      :perspectives="legendPerspectives"
      :topic-perspectives="topicPerspectives"
      active
      show-as-bar
    />
  </div>
</template>
<script lang="ts" setup>
  import { computed, ref, watch } from 'vue'
  import BaseIconButton from '../../../../core/components/base/button/icon-button/BaseIconButton.vue'
  import type { Skill } from '../../../../core/models/skill'
  import { useDisplayPercentage } from '../../../../core/composables/use-display-percentage'
  import DistributeItems from './DistributeItems.vue'
  import type { Scale } from '../../../../core/models/scale'
  import { useMembersStore } from '@/stores/members.store'
  import type { MemberOrUser } from '@/core/models/member/member-or-user'
  import BaseIcon from '../../base/icon/BaseIcon.vue'
  import SkillLegend from '../SkillLegend.vue'
  import type { DistributedPerspectiveAnalysis } from '@/core/models/surveys-analytics'
  import { SELF_PERSPECTIVE_KEY, type Perspective } from '@/core/models/perspective'
  import { analysisPerspectiveKey } from '@/core/models/perspective-analysis'
  import { resolvePerspectiveLabel } from '@/core/models/perspective-label'
  import { useI18n } from 'vue-i18n'
  import BaseMemberAvatarShaped from '../../base/avatar/BaseMemberAvatarShaped.vue'
  import PerspectiveShape from '../question-list/PerspectiveShape.vue'
  import { denormalize } from '@/core/utils/normalize.ts'

  interface Props {
    skill: Skill
    scale: Scale
    subject: MemberOrUser
    mainValue: number | null
    perspectives: ReadonlyArray<DistributedPerspectiveAnalysis>
    topicPerspectives?: ReadonlyArray<Perspective>
  }

  const props = defineProps<Props>()
  const { t, te } = useI18n()
  const memberStore = useMembersStore()

  const emits = defineEmits<{
    (e: 'skilUnselected'): void
  }>()

  const height = ref(100 / props.scale.items.length + '%')

  type PlotItem = {
    id: string
    key: string
    label: string
    member?: MemberOrUser
  }

  const items = computed(() => {
    const result: { item: PlotItem; value: number }[] = []
    const keys = perspectiveKeys.value
    if (keys.size <= 1 && !hasNonSelf.value) {
      return result
    }

    for (const entry of props.perspectives) {
      const key = analysisPerspectiveKey(entry)
      if (key === SELF_PERSPECTIVE_KEY) {
        if (entry.average != null) {
          result.push({
            item: {
              id: SELF_PERSPECTIVE_KEY,
              key: SELF_PERSPECTIVE_KEY,
              member: props.subject,
              label: labelForKey(SELF_PERSPECTIVE_KEY),
            },
            value: entry.average,
          })
        }
        continue
      }
      if (!key) continue

      for (const [index, feedback] of entry.feedbacks.entries()) {
        if (feedback.value == null) continue
        if (feedback.senderId) {
          const member = memberStore.getMemberById(feedback.senderId)
          if (member) {
            result.push({
              item: {
                id: feedback.senderId,
                key,
                member,
                label: labelForKey(key),
              },
              value: feedback.value,
            })
            continue
          }
        }
        result.push({
          item: {
            id: `anonymous-${key}-${index}`,
            key,
            label: labelForKey(key),
          },
          value: feedback.value,
        })
      }
    }
    return result
  })

  const hasNonSelf = computed(() =>
    props.perspectives.some(
      (p) => analysisPerspectiveKey(p) !== SELF_PERSPECTIVE_KEY && p.average != null,
    ),
  )

  const perspectiveKeys = computed(() => {
    const keys = new Set<string>()
    for (const entry of props.perspectives) {
      const key = analysisPerspectiveKey(entry)
      if (entry.average != null && key) {
        keys.add(key)
      }
    }
    return keys
  })

  const legendPerspectives = computed(() => {
    const keys = new Set(props.perspectives.filter((p) => p.numberOfSenders > 0).map((p) => p.key))

    return props.topicPerspectives?.filter((p) => keys.has(p.key)) ?? []
  })

  const labelForKey = (key: string) => {
    if (te(`perspectives.${key}`)) {
      return t(`perspectives.${key}`)
    }
    return resolvePerspectiveLabel(key, props.topicPerspectives, { count: 1 })
  }

  watch(
    () => props.mainValue,
    () => {
      setTimeout(() => {
        if (props.mainValue != null) {
          const percentage =
            denormalize(props.mainValue, 1, props.scale.items.length) *
            (100 / props.scale.items.length)
          height.value = `${percentage}%`
        }
      }, 10)
    },
    { immediate: true },
  )
</script>
